import { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Calendar, Loader2, Building, HelpCircle, Key } from "lucide-react";

export default function RegisterPage() {
  const [searchParams] = useSearchParams();
  const forcedRole = searchParams.get("role");
  
  const [formData, setFormData] = useState({
    cognome: "",
    nome: "",
    email: "",
    password: "",
    ruolo: forcedRole || "dipendente",
    posizione: "",
    nome_azienda: "",
    codice_invito: ""
  });
  const [loading, setLoading] = useState(false);
  const [checkingSystem, setCheckingSystem] = useState(true);
  const [isFirstSetup, setIsFirstSetup] = useState(false);
  const { register, settings, login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    checkSystemStatus();
  }, []);

  const checkSystemStatus = async () => {
    try {
      const response = await axios.get(`${API}/system/status`);
      setIsFirstSetup(response.data.is_first_setup);
      // If first setup, force role to titolare
      if (response.data.is_first_setup) {
        setFormData(prev => ({ ...prev, ruolo: "titolare" }));
      }
    } catch (error) {
      console.error("Failed to check system status");
    } finally {
      setCheckingSystem(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await register(formData);
      
      // If registering as titolare, auto-login and redirect to settings for onboarding
      if (formData.ruolo === "titolare") {
        toast.success("Account aziendale creato! Configuriamo la tua azienda...");
        const userData = await login(formData.email, formData.password);
        navigate("/settings?onboarding=true");
      } else {
        toast.success("Registrazione completata! Ora puoi accedere.");
        navigate("/login");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante la registrazione");
    } finally {
      setLoading(false);
    }
  };

  if (checkingSystem) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  // Determine available roles
  const isTitolareRegistration = isFirstSetup || forcedRole === "titolare";
  
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left side - Form */}
      <div className="flex items-center justify-center p-4 sm:p-8 lg:p-12 pb-20 sm:pb-8 bg-white">
        <div className="w-full max-w-md space-y-6 sm:space-y-8 animate-fade-in">
          {/* Logo */}
          <div className="flex flex-col items-center text-center gap-4">
            <img 
              src="/logo_ferie_permessi.jpg" 
              alt="Ferie e Permessi" 
              className="h-24 w-auto object-contain rounded-lg shadow-md"
            />
            <div>
              <h2 className="text-xl font-bold text-slate-900">Ferie e Permessi</h2>
              <p className="text-sm text-slate-500">
                {isFirstSetup ? "Configura la tua azienda" : "Gestione Aziendale Ferie e Permessi"}
              </p>
              <p className="text-xs text-slate-400 mt-1">by LuiCas ™</p>
            </div>
          </div>

          <div className="space-y-2 text-center">
            <h1 className="text-3xl font-bold tracking-tight text-slate-900">
              {isTitolareRegistration ? "Crea account aziendale" : "Registrati"}
            </h1>
            <p className="text-slate-600">
              {isTitolareRegistration 
                ? "Compila i campi per registrare la tua azienda" 
                : "Compila i campi per registrarti"}
            </p>
          </div>

          {/* First setup instructions */}
          {isFirstSetup && (
            <Card className="card-theme-highlight">
              <CardContent className="py-4">
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-theme-100 rounded-lg shrink-0">
                    <HelpCircle className="h-5 w-5 text-theme-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 text-sm">Primo passo</h3>
                    <p className="text-sm text-slate-600 mt-1">
                      Stai creando l'account <strong>Titolare</strong>. Dopo la registrazione potrai configurare le impostazioni aziendali.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="cognome" className="text-slate-700">Cognome</Label>
                <Input
                  id="cognome"
                  placeholder="Rossi"
                  value={formData.cognome}
                  onChange={(e) => setFormData({ ...formData, cognome: e.target.value })}
                  required
                  data-testid="register-cognome-input"
                  className="h-12 border-slate-200 focus:ring-2 focus:ring-slate-900"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="nome" className="text-slate-700">Nome</Label>
                <Input
                  id="nome"
                  placeholder="Mario"
                  value={formData.nome}
                  onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                  required
                  data-testid="register-nome-input"
                  className="h-12 border-slate-200 focus:ring-2 focus:ring-slate-900"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="mario.rossi@azienda.it"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                data-testid="register-email-input"
                className="h-12 border-slate-200 focus:ring-2 focus:ring-slate-900"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength={6}
                data-testid="register-password-input"
                className="h-12 border-slate-200 focus:ring-2 focus:ring-slate-900"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="posizione" className="text-slate-700">Posizione lavorativa</Label>
              <Input
                id="posizione"
                placeholder="Es: Segretaria, Commercialista, Responsabile HR"
                value={formData.posizione}
                onChange={(e) => setFormData({ ...formData, posizione: e.target.value })}
                data-testid="register-posizione-input"
                className="h-12 border-slate-200 focus:ring-2 focus:ring-slate-900"
              />
              <p className="text-xs text-slate-500">
                Opzionale - La tua mansione in azienda
              </p>
            </div>

            {/* Role selection - hidden for first setup, shown for others */}
            {isTitolareRegistration ? (
              <>
                <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
                  <div className="flex items-center gap-3">
                    <Building className="h-5 w-5 text-slate-600" />
                    <div>
                      <p className="font-medium text-slate-900">Ruolo: Titolare</p>
                      <p className="text-sm text-slate-500">Avrai accesso completo a tutte le funzionalità</p>
                    </div>
                  </div>
                </div>
                
                {/* Company name for Titolare */}
                <div className="space-y-2">
                  <Label htmlFor="nome_azienda" className="text-slate-700">Nome Azienda</Label>
                  <Input
                    id="nome_azienda"
                    placeholder="Es: Studio Rossi S.r.l."
                    value={formData.nome_azienda}
                    onChange={(e) => setFormData({ ...formData, nome_azienda: e.target.value })}
                    required
                    data-testid="register-nome-azienda-input"
                    className="h-12 border-slate-200 focus:ring-2 focus:ring-slate-900"
                  />
                  <p className="text-xs text-slate-500">
                    Questo sarà il nome visualizzato nell'app
                  </p>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-2">
                  <Label htmlFor="ruolo" className="text-slate-700">Ruolo</Label>
                  <Select
                    value={formData.ruolo}
                    onValueChange={(value) => setFormData({ ...formData, ruolo: value })}
                  >
                    <SelectTrigger className="h-12 border-slate-200" data-testid="register-ruolo-select">
                      <SelectValue placeholder="Seleziona ruolo" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="dipendente">Dipendente</SelectItem>
                      <SelectItem value="amministrativo">Amministrativo</SelectItem>
                      <SelectItem value="titolare">Titolare (nuova azienda)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    Scegli "Dipendente" per richiedere ferie/permessi, "Amministrativo" per visualizzare i dati del team, o "Titolare" per creare una nuova azienda.
                  </p>
                </div>
                
                {/* Invite code for Dipendente/Amministrativo, Company name for new Titolare */}
                {formData.ruolo === "titolare" ? (
                  <div className="space-y-2">
                    <Label htmlFor="nome_azienda" className="text-slate-700">Nome Azienda</Label>
                    <Input
                      id="nome_azienda"
                      placeholder="Es: Studio Rossi S.r.l."
                      value={formData.nome_azienda}
                      onChange={(e) => setFormData({ ...formData, nome_azienda: e.target.value })}
                      required
                      data-testid="register-nome-azienda-input"
                      className="h-12 border-slate-200 focus:ring-2 focus:ring-slate-900"
                    />
                    <p className="text-xs text-slate-500">
                      Questo sarà il nome visualizzato nell'app
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="codice_invito" className="text-slate-700 flex items-center gap-2">
                      <Key className="h-4 w-4" />
                      Codice Invito
                    </Label>
                    <Input
                      id="codice_invito"
                      placeholder="Es: ABC123"
                      value={formData.codice_invito}
                      onChange={(e) => setFormData({ ...formData, codice_invito: e.target.value.toUpperCase() })}
                      required
                      maxLength={6}
                      data-testid="register-codice-invito-input"
                      className="h-12 border-slate-200 focus:ring-2 focus:ring-slate-900 uppercase tracking-widest text-center font-mono text-lg"
                    />
                    <p className="text-xs text-slate-500">
                      Richiedi il codice al titolare della tua azienda
                    </p>
                  </div>
                )}
              </>
            )}

            <Button
              type="submit"
              disabled={loading}
              data-testid="register-submit-btn"
              className="w-full h-12 btn-theme rounded-md btn-press font-medium"
            >
              {loading ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Registrazione...</>
              ) : (
                isTitolareRegistration ? "Crea Account Aziendale" : "Registrati"
              )}
            </Button>
          </form>

          <p className="text-center text-slate-600">
            {isFirstSetup ? (
              <span className="text-sm">Hai bisogno di aiuto? Contatta il supporto</span>
            ) : (
              <>
                Hai già un account?{" "}
                <Link to="/login" className="text-slate-900 font-medium hover:underline" data-testid="login-link">
                  Accedi
                </Link>
              </>
            )}
          </p>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block relative">
        <img
          src="https://customer-assets.emergentagent.com/job_aea9fcf4-7192-4048-99b8-cf6b21f11caa/artifacts/1flrp8z8_Team.png"
          alt="Team working together"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-slate-900/20"></div>
      </div>
    </div>
  );
}
